import type { Socket } from 'socket.io-client';

import { env } from './env';

import { getAccessToken } from '~/lib/api';

/**
 * Manages a single Socket.IO connection to the API. The socket is connected
 * after login (when an access token exists) and disconnected on logout. The
 * server authenticates via the JWT access token sent in the handshake.
 *
 * Consumers subscribe to typed events via `onEvent`. We deliberately keep this
 * module tiny — Pinia stores own the application-level state, this module just
 * owns the wire.
 *
 * The `socket.io-client` payload (~70 KB gzip with engine.io) is dynamically
 * imported inside connectRealtime so the public shell (Home, Login, Forgot,
 * 404) never has to ship it. Only the type import is static.
 */
export type RealtimeEvent =
  | { type: 'notification:new'; payload: unknown }
  | { type: 'hello'; payload: { userId: string; role: string } };

type Listener = (_e: RealtimeEvent) => void;

let socket: Socket | null = null;
let connectInFlight: Promise<void> | null = null;
const listeners = new Set<Listener>();

function emit(event: RealtimeEvent): void {
  for (const l of listeners) {
    try {
      l(event);
    } catch {
      // Listener errors must never crash the socket pipeline.
    }
  }
}

export function connectRealtime(): void {
  if (socket || connectInFlight) return;
  const token = getAccessToken();
  if (!token) return;

  connectInFlight = (async () => {
    const { io: createSocket } = await import('socket.io-client');
    // Token may have rotated while the chunk was being fetched; re-read.
    const fresh = getAccessToken();
    if (!fresh) {
      connectInFlight = null;
      return;
    }
    socket = createSocket(env.VITE_API_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: { token: fresh },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10_000,
    });

    socket.on('hello', (payload: { userId: string; role: string }) => {
      emit({ type: 'hello', payload });
    });
    socket.on('notification:new', (payload: unknown) => {
      emit({ type: 'notification:new', payload });
    });
    connectInFlight = null;
  })().catch(() => {
    // Socket payload failed to load — log and stay disconnected; the user
    // experience degrades gracefully (no realtime, app still works).
    connectInFlight = null;
  });
}

export function disconnectRealtime(): void {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
}

export function onRealtime(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Test seam — replace the internal socket. */
export function __setSocketForTest(s: Socket | null): void {
  socket = s;
}
